import {Box, Modal, Title} from '@mantine/core';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {
  MdArrowBack,
  MdClose,
  MdList,
  MdOutlineBookmark,
  MdOutlineBookmarkBorder,
} from 'react-icons/md';
import {
  COLOR_PRIMARY,
  COLOR_WHITE,
} from '../../../../config/constants/colorCodes';
import {HOME_CRUMB, REPORTS_CRUMB} from '../../../../config/constants/crumb';
import {REPORT_ANALYTICS_LOGO} from '../../../../config/constants/img';
import {HOME, REPORTS} from '../../../../config/constants/routes';
import isAppWebView from '../../../../config/isAppWebView';
import {PREFERENCE_SECTION_INDEX} from '../../../../core/entities/reports/constants';
import type {ResultGetCompanyInfo} from '../../../../core/repositories/companiesRepository';
import {BreadcrumbsComponent} from '../../../../shared/components/BreadcrumbsComponent';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {createBreadCrumbsStructuredData} from '../../../../shared/services/createBreadCrumbsStructuredData';
import {getAIGradient} from '../../../reportAnalytics/usecase/getAIGradient';
import {useHandleReportSummaryUrl} from '../../usecase/useHandleReportSummaryUrl';

type Props = {
  companyInfo: ResultGetCompanyInfo | undefined;
  companyId: number;
  companyName: string;
  departmentId?: number;
  reportType: string;
  title: string;
  pageType: 'entrysheet' | 'selection_reports';
  clipCount: string;
  isClipped: boolean;
  sectionNameList: string[];
  stepNameList?: string[];
  handleClipUpdate: () => Promise<void>;
  existPreference: boolean;
  openTableOfContentsModal: () => void;
  closeTableOfContentsModal: () => void;
  isOpenTableOfContentsModal: boolean;
  hasRecruitments?: boolean;
};

const TableOfContentsModal = ({
  existPreference,
  sectionNameList,
  stepNameList,
  isOpen,
  onClose,
  hasRecruitments,
}: {
  existPreference: boolean;
  sectionNameList?: string[];
  stepNameList?: string[];
  isOpen: boolean;
  onClose: () => void;
  hasRecruitments?: boolean;
}) => {
  const stepSectionStartIndex =
    PREFERENCE_SECTION_INDEX + (existPreference ? 1 : 0);
  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      withCloseButton={false}
      styles={{
        root: {
          borderRadius: 20,
        },
      }}
    >
      <h2 className="text-md pc:text-lg font-bold border-b-[1px] border-line flex justify-between">
        <button
          type="button"
          className="p-4 hover:pointer hover:opacity-50"
          onClick={onClose}
        >
          <MdClose size={20} />
        </button>

        <span>目次</span>
        <div />
      </h2>
      <ul>
        {sectionNameList?.map((section_name, index) => (
          <li className={'mt-12'} key={section_name}>
            <a
              className="text-md hover:font-bold font-bold"
              href={`#section-${
                index <= stepSectionStartIndex
                  ? index
                  : index + (stepNameList?.length ?? 0)
              }`}
              onClick={onClose}
            >
              {section_name}
            </a>
            {index === stepSectionStartIndex && (
              <ul className="p-4">
                {stepNameList?.map((step_name, step_index) => (
                  <li key={step_name}>
                    <a
                      href={`#section-${stepSectionStartIndex + 1 + step_index}`}
                      className="block text-black-1 py-4 mx-4"
                      onClick={onClose}
                    >
                      {step_name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
        {hasRecruitments && (
          <li className="font-bold mt-12">
            <a
              href="#recommend-recruitments"
              className="text-md hover:font-bold"
              onClick={onClose}
            >
              この体験記を見ている方におすすめの募集
            </a>
          </li>
        )}
      </ul>
    </Modal>
  );
};

export const ReportDetailHeader: React.FC<Props> = ({
  companyInfo,
  companyId,
  companyName,
  departmentId,
  reportType,
  title,
  pageType,
  clipCount = 0,
  isClipped,
  handleClipUpdate,
  sectionNameList,
  stepNameList,
  existPreference,
  openTableOfContentsModal,
  closeTableOfContentsModal,
  isOpenTableOfContentsModal,
  hasRecruitments,
}) => {
  const router = useRouter();
  const crumbs = [
    {label: HOME_CRUMB, href: HOME},
    {label: REPORTS_CRUMB, href: REPORTS},
    {
      label: companyInfo?.ok ? `${companyInfo.value.industryCategoryName}` : '',
      href: `/reports/industry/${companyInfo?.ok ? companyInfo.value.industryCategoryID : ''}`,
    },
    {label: companyName, href: `/company/${companyId}/experiences`},
    {
      label: title.replace(companyName, ''),
      href: '',
    },
  ];

  const {reportSummaryUrl} = useHandleReportSummaryUrl({
    selectionReportType: reportType,
    companyId,
    departmentId,
  });

  return (
    <Box className="w-full bg-white pc:bg-background-1 py-8 mb-8 pc:w-[1200px] pc:pt-8">
      <Head>
        <script type="application/ld+json">
          {JSON.stringify(createBreadCrumbsStructuredData(crumbs))}
        </script>
      </Head>
      <Box className="h-28 font-bold hidden pc:block">
        <BreadcrumbsComponent crumbs={crumbs} />
      </Box>
      {isAppWebView() ? null : (
        <Box className="pt-8 pc:hidden">
          <CommonLink href={`/company/${companyId}/experiences`}>
            <MdArrowBack
              className="bg-background rounded-[20px] p-4 mx-8 my-4"
              size={24}
            />
          </CommonLink>
        </Box>
      )}
      <Box className="flex flex-col mb-8 items-center justify-between gap-x-12 bg-white px-16 py-4 pc:shadow-card pc:my-16 pc:h-[76px] pc:flex-row pc:rounded-[16px]">
        <Title
          order={1}
          className="text-lg pc:text-xl text-black leading-relaxed mb-8 pc:mr-16"
        >
          {title}
        </Title>
        <Box className="flex w-full justify-between pc:w-auto">
          <Box className="flex gap-8">
            <Box
              className="bg-primary-1 text-white text-base rounded-[20px] flex items-center px-8 pc:hidden"
              onClick={openTableOfContentsModal}
            >
              <div className="flex h-20">
                <MdList size={20} /> <span>目次</span>
              </div>
            </Box>
            <button
              className={`rounded-[28px] border-[2px] text-center border-primary flex items-center px-16 pc:py-8 ${isClipped ? 'bg-primary text-white' : 'bg-white text-primary'}`}
              type="button"
              onClick={() => {
                void handleClipUpdate();
              }}
            >
              {isClipped ? (
                <MdOutlineBookmark color={COLOR_WHITE} size="20px" />
              ) : (
                <MdOutlineBookmarkBorder color={COLOR_PRIMARY} size="20px" />
              )}
              <span className={'text-base no-wrap mx-8 w-64'}>クリップ</span>
              <span className={'text-base no-wrap'}>{clipCount}</span>
            </button>
          </Box>
          {pageType === 'entrysheet' ? null : (
            <Box
              className={
                'rounded-[32px] w-[94px] h-[32px] flex items-center justify-center cursor-pointer ml-12 pc:w-[148px] pc:h-[44px] '
              }
              onClick={() => {
                if (isAppWebView()) {
                  location.href = reportSummaryUrl;
                } else {
                  void router.push(reportSummaryUrl);
                }
              }}
              style={{
                background: getAIGradient(),
              }}
            >
              <img src={REPORT_ANALYTICS_LOGO} className="w-[54px] h-[20px]" />
            </Box>
          )}
        </Box>
        <TableOfContentsModal
          existPreference={existPreference}
          sectionNameList={sectionNameList}
          stepNameList={stepNameList}
          isOpen={isOpenTableOfContentsModal}
          onClose={closeTableOfContentsModal}
          hasRecruitments={hasRecruitments}
        />
      </Box>
    </Box>
  );
};
